import { useRef, useState, type ReactNode, type Ref } from 'react';
import { ModalAction } from '@overlayqa/shared/ui';
import { INTEGRATION_STATUS_UNAVAILABLE_COPY } from '@overlayqa/shared';
import { capture } from '@/lib/analytics';
import { IntegrationCardSkeleton } from './IntegrationCardSkeleton';
import { IntegrationDialog } from './IntegrationDialog';
import type { IntegrationKey } from './integrationDetails';
import { IntegrationStatusChip, integrationStatus, type IntegrationConnection } from './integrationStatus';

export { IntegrationStatusChip, integrationStatus, type IntegrationConnection, type IntegrationStatus } from './integrationStatus';

interface IntegrationCardShellProps {
  tile: ReactNode;
  /** Header-right: the status chip, Retry, a busy button, or Set up. Sits above the card's own button. */
  right: ReactNode;
  name: ReactNode;
  description: ReactNode;
  descriptionId: string;
  onOpen: () => void;
  openerRef?: Ref<HTMLButtonElement>;
  children?: ReactNode;
  testId?: string;
}

/**
 * The one card on /integrations (design system: card.integration): tile and status in the header row, then the name
 * and its description. The name is the card's button, stretched over the whole card, so a click anywhere opens the
 * integration's dialog; the header-right control sits above it. Every provider and tool card renders through it.
 */
export function IntegrationCardShell({ tile, right, name, description, descriptionId, onOpen, openerRef, children, testId }: IntegrationCardShellProps) {
  return (
    <div className="card-sm relative flex flex-col gap-3 h-full transition-[border-color,box-shadow] hover:border-gray-300 hover:shadow-[shadow:var(--oq-elevation-hover)]" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        {tile}
        <span className="relative z-10 inline-flex items-center min-h-[var(--oq-control-app-standard-height)]">{right}</span>
      </div>
      <div className="flex-1">
        <button ref={openerRef} type="button" onClick={onOpen} aria-haspopup="dialog" aria-describedby={descriptionId}
          className="font-medium text-gray-900 text-left after:absolute after:inset-0 after:rounded-lg after:content-[''] focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-primary-500 focus-visible:after:ring-offset-2">
          {name}
        </button>
        <p id={descriptionId} className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
      {children}
    </div>
  );
}

interface IntegrationCardProps {
  provider: IntegrationKey;
  /** The 24px logo tile (IntegrationTile); the dialog's title carries it too. */
  tile: ReactNode;
  name: string;
  description: ReactNode;
  connection: IntegrationConnection;
  /** The status read is in flight: the card shows its skeleton, an open dialog stays open. */
  loading?: boolean;
  /** Action failures in order; empty entries render nothing. */
  errors?: Array<string | null | undefined>;
  /** Extra rows in the dialog's detail card while connected (Slack's Channel row). */
  children?: ReactNode;
  testId?: string;
}

/** A provider card and its details dialog: Connect, Reconnect and Disconnect live in the dialog. */
export function IntegrationCard({ provider, tile, name, description, connection, loading = false, errors = [], children, testId }: IntegrationCardProps) {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const live = integrationStatus(connection);
  // A re-read with nothing cached reads as disconnected while it is pending; the dialog keeps the last answer.
  const settled = useRef(live);
  if (!loading) settled.current = live;
  const status = loading ? settled.current : live;
  const shownErrors = errors.filter((e): e is string => !!e);

  const close = () => {
    setOpen(false);
    opener.current?.focus();
  };

  const right = status === 'unknown'
    ? <button type="button" onClick={connection.onRetry} disabled={loading} className="btn-secondary text-sm"><span className="text-cap-trim">Retry</span></button>
    : connection.connecting
      ? <button type="button" disabled className="btn-primary text-sm"><ModalAction kind="link">Connecting...</ModalAction></button>
      : connection.disconnecting
        ? <button type="button" disabled className="btn-secondary text-sm"><span className="text-cap-trim">Disconnecting...</span></button>
        : <IntegrationStatusChip status={status} />;

  return (
    <>
      {loading ? <IntegrationCardSkeleton /> : (
        <IntegrationCardShell testId={testId} tile={tile} right={right} name={name} description={description}
          descriptionId={`integration-${provider}-description`} openerRef={opener}
          onOpen={() => {
            capture('integration_card_opened', { provider, status });
            setOpen(true);
          }}>
          {status === 'unavailable' && connection.unavailableReason && <p className="text-xs text-gray-500">{connection.unavailableReason}</p>}
          {/* While the dialog is open it announces these itself; the card keeps the line but not a second alert. */}
          {status === 'unknown' && <p className="text-xs text-red-600" role={open ? undefined : 'alert'}>{INTEGRATION_STATUS_UNAVAILABLE_COPY}</p>}
          {shownErrors.map((error) => <p key={error} className="text-xs text-red-600" role={open ? undefined : 'alert'}>{error}</p>)}
        </IntegrationCardShell>
      )}
      {open && (
        <IntegrationDialog provider={provider} name={name} tile={tile} status={status} connection={connection}
          loading={loading} errors={shownErrors} onClose={close}>
          {children}
        </IntegrationDialog>
      )}
    </>
  );
}
